import React, { useCallback, useEffect, useMemo } from 'react'
import { setCookie } from 'cookies-next'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { LinkButton } from '@/atoms/Button'
import { LivestreamIcon } from '@/atoms/Icons/LivestreamIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { PlaySquareIcon } from '@/atoms/Icons/PlaySquareIcon'
import { StoreIcon } from '@/atoms/Icons/StoreIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { AsH1, CaptionMD, CaptionSM, EyebrowMD, TitleSM, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants'
import { CONTENT_SHARED_ID } from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { CatalogTitle, findHydraExternalId, findTitleProjectSlug } from '@/services/ContentCatalog'
import { claimOneToManyShareLink, ShareLink } from '@/services/ContentShareService'
import { useUser } from '@/services/UserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { buildLoginUrl, getAccessTokenFromClient } from '@/utils/auth-utils'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '../GuildJoinView/GuildEmailInput'

export interface ContentSharedViewDisplayFields {
  title: string
  subTitle?: string
}

interface ContentSharedViewProps {
  shareLink: ShareLink
  watchableTitle: CatalogTitle
  displayFields: ContentSharedViewDisplayFields
}

export const OneToManyContentSharedView: React.FC<ContentSharedViewProps> = ({
  shareLink,
  watchableTitle,
  displayFields,
}) => {
  const track = useSafeTrack()
  const { t } = useTranslate('watch')
  const { isLoggedIn, userId } = useUser()
  const router = useSafeRouter()

  const slug = useMemo(() => findTitleProjectSlug(watchableTitle), [watchableTitle])
  const hydraId = useMemo(() => findHydraExternalId(watchableTitle), [watchableTitle])

  const loginUrl = buildLoginUrl(router.asPath)
  const projectType =
    watchableTitle.type === 'movie'
      ? t('filmShare', 'film')
      : watchableTitle.type === 'special'
      ? t('specialShare', 'special')
      : t('showShare', 'show')

  const redirect = useCallback(() => {
    if (slug && hydraId) {
      router.replace(`${paths.watch.index}/${slug}/episode/${hydraId}`)
    } else {
      logger().error(`Cannot redirect to the shared watchable directly because slug and or id are missing!`, {
        watchableTitle,
        slug,
        hydraId,
      })
      router.replace(paths.watch.index)
    }

    const html = window.document.getElementsByTagName('html')[0]
    setTimeout(() => {
      html.style.height = 'initial'
      html.style.overflow = 'initial'
    }, 100)
  }, [router, slug, hydraId, watchableTitle])

  useEffect(() => {
    if (!slug || !hydraId) {
      logger().error(`Cannot claim a share link because the content catalog title is missing a slug or id!`, {
        slug,
        hydraId,
        watchableTitle,
        shareLink,
      })
    }
  }, [slug, hydraId, watchableTitle, shareLink, t])

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      setCookie(CONTENT_SHARED_ID, shareLink.id, { maxAge: Durations.ONE_WEEK_IN_SECONDS })
      return
    }

    // Check if this is the same user that created the share link
    if (userId === shareLink.user_id) return redirect()

    const accessToken = getAccessTokenFromClient()
    claimOneToManyShareLink({ accessToken, shareLinkId: shareLink.id })
      .then((claim) => {
        track('Guild Content Share Link Claimed', {
          shareLink,
          claim,
          sharerUserId: shareLink.user_id,
          claimerUserId: userId,
          titleId: shareLink.content_id,
          type: 'one-to-many',
        })

        redirect()
      })
      .catch((err) => {
        logger().error('An error occurred when trying to claim a share link!', { shareLink }, err)
      })
  }, [isLoggedIn, userId, redirect, shareLink, track])

  const handleGuildJoinButtonClick = useCallback(() => {
    track('Guild Content Share Link Join Guild Clicked', {
      shareLink,
      titleId: shareLink.content_id,
      type: 'one-to-many',
    })
  }, [shareLink, track])

  const guildBenefits = [
    { title: t('ourFullLibrary', 'Our full library of shows'), icon: <PlaySquareIcon size={24} color="copper" /> },
    { title: t('angelMovieTicketsV2', 'Angel movie tickets'), icon: <TicketIcon size={24} color="copper" /> },
    { title: t('guildOnlyLivestreamsV2', 'Guild only livestreams'), icon: <LivestreamIcon size={24} color="copper" /> },
    { title: t('merchDiscountsV2', 'Merch discounts'), icon: <StoreIcon size={24} color="copper" /> },
  ]

  return (
    <main>
      <div className="w-full overflow-hidden bg-core-gray-950 px-6 py-20 text-white md:bg-[url('https://images.angelstudios.com/image/upload/v1741295068/angel-studios/backgrounds/Background_Image_1.png')] md:bg-cover md:bg-center md:px-12 md:py-24 lg:py-32">
        <div className="m-auto flex w-fit flex-col">
          <div className="flex flex-col items-center gap-4 lg:gap-6 xl:flex-row">
            <div className="block max-w-[440px] shrink-0 overflow-hidden rounded-lg lg:hidden">
              <Image
                className="h-full w-full rounded-2xl"
                src={getCloudinaryImageUrl({ path: shareLink.metadata.opengraph_image, width: 300, height: 150 })}
                alt={t('moviePosterAlt', '{{name}} Movie Poster', { name: displayFields.title })}
                width={0}
                height={0}
                sizes="100vw"
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
            <div className="flex flex-col items-center gap-2 text-center lg:gap-6">
              <EyebrowMD className="hidden text-[13px] lg:flex" weight="semibold" color="core-gray-300">
                {t('watchForFree', 'Watch for Free')}
              </EyebrowMD>
              <TitleSM
                as={AsH1}
                weight="bold"
                className="md:photon-title-md lg:photon-heading-md 2xl:photon-heading-lg"
              >
                {t('youHaveBeenInvitedToWatch', `You've been invited to watch {{ name }}.`, {
                  name: displayFields.title,
                })}
              </TitleSM>
              <CaptionSM
                className="md:photon-caption-md lg:photon-caption-lg mb-4 max-w-[622px] lg:mb-8"
                color="core-gray-400"
              >
                {t(
                  'angelGuildMemberSharedExclusiveCreateFree',
                  'An Angel Guild member has shared this exclusive {{ projectType }} with you. Create your FREE account to start watching immediately.',
                  { projectType },
                )}
              </CaptionSM>
              <div className="hidden max-w-[440px] shrink-0 overflow-hidden rounded-lg lg:block">
                <Image
                  className="h-full w-full rounded-2xl"
                  src={getCloudinaryImageUrl({ path: shareLink.metadata.opengraph_image, width: 300, height: 150 })}
                  alt={t('moviePosterAlt', '{{name}} Movie Poster', { name: displayFields.title })}
                  width={0}
                  height={0}
                  sizes="100vw"
                  style={{ width: '100%', height: 'auto' }}
                />
              </div>
              <LazyHydrate whenVisible>
                <>
                  <When condition={!isLoggedIn}>
                    <div className="flex w-full max-w-[600px] flex-col items-center gap-2 lg:mt-8 lg:items-start">
                      <GuildEmailInput loginOrigin="shared" variant="centered-white" contentShareId={shareLink.id} />
                      <CaptionMD className="mx-4 my-2" weight="semibold">
                        <Translate i18nKey="alreadyAngelMemberLogin" t={t}>
                          Already an Angel Member?{' '}
                          <a href={loginUrl} className="text-oxide-primary">
                            Login
                          </a>
                        </Translate>
                      </CaptionMD>
                    </div>
                  </When>
                  <When condition={isLoggedIn}>
                    <div className="flex min-h-[136px] w-full max-w-[600px] flex-col items-center gap-4 md:min-h-[120px] md:gap-2 lg:mt-8 lg:min-h-[98px]">
                      <TitleXS weight="bold" className="md:photon-title-sm lg:photon-title-md xl:photon-title-lg">
                        {t('claimLinkLoading', 'Preparing to watch...')}
                      </TitleXS>
                      <LoadingSpinnerIcon size={48} color="white" />
                    </div>
                  </When>
                </>
              </LazyHydrate>
            </div>
          </div>
        </div>
        <div className="mt-4 flex w-full flex-col items-start justify-center border-t border-core-gray-700 pt-4 md:items-center md:border-t-0 lg:pt-8">
          <TitleXS weight="semibold" className="mb-4">
            {t('joinTheMovementForExclusiveAccess', 'Join the movement and get exclusive access to:')}
          </TitleXS>
          <div className="flex w-fit flex-col gap-4 lg:flex-row lg:gap-6">
            {guildBenefits?.map((benefit, index) => (
              <div key={index} className="flex w-full min-w-fit flex-row items-center gap-2">
                {benefit.icon}{' '}
                <CaptionMD color={'core-gray-400'} className="w-fit">
                  {benefit.title}
                </CaptionMD>
              </div>
            ))}
          </div>
          <When condition={!isLoggedIn}>
            <LinkButton
              href={paths.guild.join}
              onClick={handleGuildJoinButtonClick}
              variant="core-gray-800"
              className="mt-4 w-full rounded-lg px-6 py-[14px] md:w-fit lg:mt-6"
            >
              {t('getStarted', 'Get Started')}
            </LinkButton>
          </When>
        </div>
      </div>
    </main>
  )
}
